import type { RelativeWorkspacePath } from "@codesandbox/pitcher-client";
import { PICKER_BROWSER_PACKAGE_MANAGER } from "@codesandbox/pitcher-client/dist/esm/browser";
import { CustomSidebarView } from "components/Editor/CommonVSCode/CustomSidebarView";
import { relativePathToResource } from "environment-interface";
import { useEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { useTasks } from "hooks/pitcher/useTasks";
import { debounce } from "lodash";
import { Badge, Box, Button, Stack, Text, Tooltip, styled } from "prism-react";
import { useCallback, useEffect, useState } from "react";
import * as vscode from "vscode";
import { DisposableStore } from "vscode/monaco";

import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";
import { executeVSCodeCommand } from "utils/vscode";

import { SearchDependency } from "./SearchDependency";
import type { Dependency, DependencyList, Props } from "./types";
import type { PackageManager } from "./utils";
import {
  detectPackageManager,
  findTaskStatus,
  getDependencyLatestVersion,
  getInstallationScript,
  getUninstallScript,
  onSearchDependency,
} from "./utils";

const ROOT_PACKAGE_MANAGER = "/package.json" as RelativeWorkspacePath;

export const DependencyManager: React.FC = () => {
  const editor = useEditor();
  const pitcher = usePitcher();
  const tasks = useTasks();
  const { state: setupProgressState } = useSetupProgress();
  const [parsingError, setParsingError] = useState(false);
  const isDevbox = editor.type === "sandbox" && editor.isCloud;
  const isBrowserSandbox =
    editor.type === "sandbox" && editor.isCloud === false;
  const [enabled, setEnabled] = useState(true);

  const [dependencies, setDependencies] = useState<DependencyList>([]);
  const [projectDependencyManager, setProjectDependencyManager] =
    useState<PackageManager>("yarn");
  const [pendingQueueTasks, setPendingQueueTasks] = useState<string[]>([]);

  const getPackageManagerContent = async (doc: vscode.TextDocument) => {
    const content = doc.getText();

    try {
      setParsingError(false);
      const json = JSON.parse(content) as Record<
        string,
        Record<string, string>
      >;

      const deps = Object.entries(json.dependencies ?? {}).map(
        ([name, version]) => ({ name, version, dev: false }),
      );
      const devDeps = Object.entries(json.devDependencies ?? {}).map(
        ([name, version]) => ({ name, version, dev: true }),
      );

      setDependencies(isDevbox ? [...deps, ...devDeps] : deps);
    } catch {
      setParsingError(true);
    }
  };

  const watchPackageManager = async () => {
    const resourceUri = relativePathToResource(ROOT_PACKAGE_MANAGER);
    const disposable = vscode.workspace.onDidChangeTextDocument((ev) => {
      if (ev.document.uri.toString() === resourceUri.toString()) {
        getPackageManagerContent(ev.document);
      }
    });

    try {
      const document = await vscode.workspace.openTextDocument(resourceUri);

      getPackageManagerContent(document);
    } catch {
      // It's fine, ROOT_PACKAGE_MANAGER doesn't exist
      setEnabled(false);
    }

    return disposable;
  };

  const detectPackageManagerDebounced = debounce(() => {
    if (isBrowserSandbox) {
      setProjectDependencyManager(PICKER_BROWSER_PACKAGE_MANAGER);
      return;
    }

    detectPackageManager(async (relativePath: string) => {
      const resource = relativePathToResource(relativePath);

      try {
        const stat = await vscode.workspace.fs.stat(resource);

        return stat.type === vscode.FileType.File;
      } catch (e) {
        return false;
      }
    }).then((packageManager) => setProjectDependencyManager(packageManager));
  }, 1000);

  useVSCodeExtensionInitializedEffect(() => {
    detectPackageManagerDebounced();

    const watcherDisposableStore = new DisposableStore();
    const watcher = watcherDisposableStore.add(
      vscode.workspace.createFileSystemWatcher("package.json"),
    );
    watcherDisposableStore.add(
      watcher.onDidCreate(() => {
        detectPackageManagerDebounced();
      }),
    );
    watcherDisposableStore.add(
      watcher.onDidDelete(() => {
        detectPackageManagerDebounced();
      }),
    );

    const disposablePromise = watchPackageManager();
    return () => {
      disposablePromise.then((disposable) => disposable.dispose());
      watcherDisposableStore.dispose();
    };
  }, []);

  useEffect(
    function closeCompletedTasks() {
      for (const taskId of pendingQueueTasks) {
        const status = tasks[taskId]?.shell?.status;

        if (status === "FINISHED") {
          // remove from queue
          setPendingQueueTasks((prev) =>
            prev.filter((item) => item !== taskId),
          );
        }
      }
    },
    [pendingQueueTasks, tasks],
  );

  const onInstallDependency = async (
    dependencyName: string,
    version: string,
    dev: boolean,
  ) => {
    // Optimistic update the UI
    setDependencies((prev) => {
      // Don't add duplicated
      if (prev.find((item) => item.name === dependencyName)) {
        return prev;
      }

      return [{ name: dependencyName, version, dev }, ...prev];
    });

    const command = getInstallationScript({
      manager: projectDependencyManager,
      dependency: dependencyName,
      version,
      dev,
    });

    const { id } = await pitcher.clients.task.runCommand(
      command,
      false,
      `Installing ${dependencyName}`,
    );

    setPendingQueueTasks((prev) => [...prev, id]);
  };

  const onUpgradeDependency = async (dependencyName: string, dev: boolean) => {
    const latest = await getDependencyLatestVersion(dependencyName);

    const command = getInstallationScript({
      manager: projectDependencyManager,
      dependency: dependencyName,
      dev,
      version: latest,
    });

    const { id } = await pitcher.clients.task.runCommand(
      command,
      false,
      `Upgrading ${dependencyName}`,
    );
    setPendingQueueTasks((prev) => [...prev, id]);
  };

  const onRemoveDependency = async (dependencyName: string) => {
    const command = getUninstallScript(
      projectDependencyManager,
      dependencyName,
    );

    const { id } = await pitcher.clients.task.runCommand(
      command,
      false,
      `Removing ${dependencyName}`,
    );

    setPendingQueueTasks((prev) => [...prev, id]);
  };

  const onOpenProcess = (taskId: string) => {
    executeVSCodeCommand("codesandbox.terminal.open", {
      taskId,
    });
  };

  const onRetryDependency = (taskId: string) => {
    pitcher.clients.task.runTask(taskId);

    onOpenProcess(taskId);
  };

  const findTaskStatusByDependency = useCallback(
    (dependency: Dependency) => {
      return findTaskStatus(tasks, projectDependencyManager, dependency);
    },
    [tasks, projectDependencyManager],
  );

  if (!enabled) {
    return null;
  }

  return (
    <CustomSidebarView
      viewContainerId="workbench.view.explorer"
      viewOptions={{
        id: "codesandbox.dependencies",
        location: 0,
        name: "Dependencies",
        order: 1,
        icon: "csb-icon://sandbox",
        collapsed: false,
        canToggleVisibility: true,
      }}
    >
      <DependencyManagerInterface
        dependencies={dependencies}
        disabled={setupProgressState !== "FINISHED"}
        findTaskStatus={findTaskStatusByDependency}
        onInstallDependency={onInstallDependency}
        onOpenProcess={onOpenProcess}
        onRemoveDependency={onRemoveDependency}
        onRetryDependency={onRetryDependency}
        onSearchDependency={onSearchDependency}
        onUpgradeDependency={onUpgradeDependency}
        parsingError={parsingError}
      />
    </CustomSidebarView>
  );
};

type DependencyManagerInterfaceProps = Pick<
  Props,
  | "dependencies"
  | "onInstallDependency"
  | "onRemoveDependency"
  | "onSearchDependency"
  | "onUpgradeDependency"
  | "findTaskStatus"
  | "onRetryDependency"
  | "onOpenProcess"
  | "disabled"
  | "parsingError"
>;
const DependencyManagerInterface: React.FC<DependencyManagerInterfaceProps> = ({
  dependencies,
  onInstallDependency,
  onRemoveDependency,
  onSearchDependency,
  onUpgradeDependency,
  findTaskStatus,
  onRetryDependency,
  onOpenProcess,
  disabled,
  parsingError,
}) => {
  return (
    <StyledWrapper>
      <Box
        css={{
          padding: "$3 0",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box css={{ px: "$4" }}>
          <SearchDependency
            disabled={disabled}
            onDependencySelect={onInstallDependency}
            onSearchDependency={onSearchDependency}
          />
        </Box>

        <Stack css={{ overflow: "auto", height: "100%", px: "$4" }} gap={0}>
          {parsingError ? (
            <Text
              as="p"
              color="informative-error-base"
              css={{
                marginTop: "$3",
                textAlign: "center",
                width: "100%",
                button: {
                  color: "inherit",
                  textDecoration: "underline",
                  "&:hover": { textDecoration: "none" },
                },
              }}
            >
              Unable to parse{" "}
              <button
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "FILE",
                    filepath: ROOT_PACKAGE_MANAGER,
                  });
                }}
              >
                package.json
              </button>
            </Text>
          ) : (
            dependencies.map(({ name, version, dev }) => {
              const { status, id } = findTaskStatus({ name, version, dev });

              return (
                <DependencyItem key={name} gap={1} horizontal>
                  <Stack css={{ minWidth: 0 }} horizontal>
                    <Text css={{ minWidth: 40 }} truncate>
                      {name}
                    </Text>
                    {status === "FINISHED" && (
                      <Text
                        as="pre"
                        css={{
                          fontSize: ".9em",
                          opacity: 0.7,
                        }}
                        // If the version is a typical semver format (ˆ00.00.00)
                        // we don't truncate it, but do so if it's a
                        // long format like experimental-commitsha. It's not perfect
                        // but it should cover 90% of the cases
                        truncate={version.length > 9}
                      >
                        {version}
                      </Text>
                    )}
                    {dev && (
                      <Badge
                        css={{ height: "$4", px: 6 }}
                        textCss={{ fontSize: "10px" }}
                      >
                        DEV
                      </Badge>
                    )}
                  </Stack>

                  {status === "ERROR" ? (
                    <Tooltip content="Retry and open terminal">
                      <Button
                        css={{ color: "$informative-error-base" }}
                        disabled={disabled}
                        icon="circleBang"
                        iconSize={4}
                        onClick={() => {
                          if (id) {
                            onRetryDependency(id);
                          }
                        }}
                      />
                    </Tooltip>
                  ) : status === "RUNNING" ? (
                    <Button
                      icon="progress"
                      iconSize={4}
                      onClick={() => {
                        if (id) {
                          onOpenProcess(id);
                        }
                      }}
                    />
                  ) : (
                    <Stack
                      className="dependency_button"
                      css={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        opacity: 0,
                      }}
                      gap={1}
                      horizontal
                    >
                      <Tooltip content="Open on NPM">
                        <Button
                          as="a"
                          href={`https://www.npmjs.com/package/${name}`}
                          icon="externalLink"
                          iconSize={3}
                          target="_blank"
                        />
                      </Tooltip>

                      <Tooltip content="Update to latest">
                        <Button
                          disabled={disabled}
                          icon="refresh"
                          iconSize={3}
                          onClick={() => {
                            onUpgradeDependency(name, dev);
                          }}
                        />
                      </Tooltip>

                      <Tooltip content="Uninstall">
                        <Button
                          disabled={disabled}
                          icon="trash"
                          onClick={() => {
                            onRemoveDependency(name);
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  )}
                </DependencyItem>
              );
            })
          )}
        </Stack>
      </Box>
    </StyledWrapper>
  );
};

const DependencyItem = styled(Stack, {
  justifyContent: "space-between",
  width: "100%",
  minHeight: 28,
  position: "relative",

  "&:focus-within .dependency_button": {
    position: "static",
    opacity: 1,
  },
  "&:hover .dependency_button": {
    position: "static",
    opacity: 1,
  },
});

const StyledWrapper = styled("div", {
  display: "block",
  width: "100%",
});
